import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Post.css";
import { timeAgo } from "../utils/timeAgo";

// -----------------------------
// Compact monochrome SVG icons
// -----------------------------
const ICON_ELLIPSIS = "\u22EE";

function ReplyIcon() {
  return (
    <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
      <path
        d="M21 12c0 4.418-4.03 8-9 8-1.174 0-2.295-.2-3.313-.565L3 21l1.857-4.334C4.31 15.38 3 13.77 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RepostIcon() {
  return (
    <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
      <path
        d="M7 7h9l-2.5-2.5M17 17H8l2.5 2.5M16 7l3 3-3 3M8 17l-3-3 3-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LikeIcon({ active = false }) {
  return (
    <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
      <path
        d="M12 20s-6.5-4.35-8.5-7.2C1.7 10.27 2.2 6.9 5.3 5.6c2.04-.85 4.08-.07 5.2 1.35 1.12-1.42 3.16-2.2 5.2-1.35 3.1 1.3 3.6 4.67 1.8 7.2C18.5 15.65 12 20 12 20Z"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ViewsIcon() {
  return (
    <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
      <path
        d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
      <path
        d="M14 5h5v5M10 14 19 5M19 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ICON_DELETE = "\u{1F5D1}";
const ICON_WARN = "\u26A0\uFE0F";
const ICON_BLOCK = "\u26D4";
const ICON_NOT_INTERESTED = "\u{1F44E}";
const ICON_MUTE = "\u{1F6AB}";

// -----------------------------
// Helpers
// -----------------------------
function linkify(text) {
  if (!text) return "";
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
}

function findFirstYouTubeUrl(text) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urls = text.match(urlRegex) || [];
  return urls.find((u) => /youtube\.com|youtu\.be/i.test(u)) || null;
}

function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const match = url.match(
      /(?:youtube\.com\/.*v=|youtu\.be\/)([a-zA-Z0-9_-]{6,})/
    );
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

function getSafeApiBase() {
  const base = process.env.REACT_APP_API_URL || "";
  if (!base) return "";

  try {
    if (typeof window !== "undefined" && window.location?.protocol === "https:") {
      if (base.startsWith("http://")) return base.replace(/^http:\/\//, "https://");
    }
  } catch (e) {}

  return base;
}

function getPermalinkUrl(postId) {
  try {
    const origin = window.location.origin;
    return `${origin}/backend/p/${postId}`;
  } catch (e) {
    return `/backend/p/${postId}`;
  }
}

function getViewUrl(apiBase, postId) {
  if (apiBase && apiBase.endsWith("/backend")) {
    return `${apiBase}/posts/${postId}/view`;
  }
  if (apiBase) {
    return `${apiBase}/backend/posts/${postId}/view`;
  }
  return `/backend/posts/${postId}/view`;
}

const COMMUNITY_CATEGORIES = [
  "General",
  "AI",
  "Crypto",
  "Economy",
  "Entertainment",
  "Finance",
  "Health",
  "Media",
  "News",
  "Politics",
  "Science",
  "Sports",
  "Technology",
  "Travel",
  "Weather",
  "World",
];

function getPostCommunityCategory(post) {
  const value = post?.communityCategory || post?.category || "";
  return COMMUNITY_CATEGORIES.includes(value) ? value : "";
}

function getCommunityShortLabel(category) {
  return COMMUNITY_CATEGORIES.includes(category) ? category : "";
}

function Post({
  post,
  currentUser,
  onDelete,
  onLike,
  onReply,
  onRepost,
  onNavigateToProfile,
  onShowModal,
  extraActions,
  onMuteUser = () => {},
  onNotInterested = null,
  onReport = null,
  onBlockUser = null,
  showCommunityLabel = true,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const [localViewCount, setLocalViewCount] = useState(
    post.view_count ?? post.viewCount ?? 0
  );
  const [mediaNearViewport, setMediaNearViewport] = useState(false);

  const dropdownRef = useRef(null);
  const postRef = useRef(null);
  const videoRef = useRef(null);
  const viewTrackedRef = useRef(false);

  const navigate = useNavigate();
  const API_BASE = getSafeApiBase();

  const isCurrentUser = currentUser?.username === post.username;
  const isAdminOrMod = Number(currentUser?.group_id) === 1 || Number(currentUser?.group_id) === 2;
  const shouldShowEllipsis = isCurrentUser || isAdminOrMod || !!currentUser;

  // Administrative moderation uses the existing server-side Group 11 system.
  const handleAdminModeration = async (action) => {
    if (Number(currentUser?.group_id) !== 1 || !post.user_id) return;
    const username = post.username || `user ${post.user_id}`;
    let reason;
    if (action === "warnings") {
      reason = window.prompt(`Warning reason for @${username}:`);
      if (reason === null) return;
      if (!reason.trim()) { window.alert("A warning reason is required."); return; }
    } else if (!window.confirm(`Block @${username} from Scoldy (Group 11)?`)) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/admin/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: post.user_id, ...(reason ? { reason: reason.trim() } : {}) }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        window.alert(`Moderation failed: ${data.error || data.details || response.status}`);
        return;
      }
      window.alert(action === "warnings"
        ? `Warning recorded for @${username}. Total warnings: ${data.totalWarnings}.${data.blocked ? " Account blocked (Group 11)." : ""}`
        : `@${username} blocked (Group 11).`);
      setShowDropdown(false);
    } catch (error) {
      window.alert(`Moderation request failed: ${error.message}`);
    }
  };


  const isGuest = (() => {
    if (!currentUser) return true;
    if (currentUser.isGuest === true) return true;
    if (currentUser.guest === true) return true;
    if (currentUser.role === "guest") return true;
    try {
      if (localStorage.getItem("isGuest") === "true") return true;
    } catch (e) {}
    return false;
  })();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Use one observer per post for both media activation and view tracking.
  useEffect(() => {
    if (!postRef.current) return;

    const userId = parseInt(currentUser?.id, 10);
    const hasTrackableUser = userId && !Number.isNaN(userId);
    const viewUrl = hasTrackableUser ? getViewUrl(API_BASE, post.id) : null;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (!entry) return;

        setMediaNearViewport(entry.isIntersecting);

        if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
        if (!hasTrackableUser || viewTrackedRef.current) return;

        viewTrackedRef.current = true;

        try {
          const resp = await fetch(viewUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ userId }),
          });

          if (resp.ok) {
            const data = await resp.json().catch(() => null);

            if (typeof data?.view_count === "number") {
              setLocalViewCount(data.view_count);
              return;
            }

            if (data?.inserted === true) {
              setLocalViewCount((v) => (typeof v === "number" ? v + 1 : 1));
            }
          }
        } catch (e) {
          // silent
        }
      },
      {
        rootMargin: "500px 0px",
        threshold: [0, 0.6],
      }
    );

    observer.observe(postRef.current);
    return () => observer.disconnect();
  }, [post.id, currentUser?.id, API_BASE]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (mediaNearViewport) {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      video.pause();
    }
  }, [mediaNearViewport]);

  if (isHidden) return null;

  const handlePostClick = (e) => {
    if (e.target.closest(".home-post-actions")) return;
    if (e.target.closest(".home-post-ellipsis-wrapper")) return;
    if (e.target.closest("button")) return;
    if (e.target.closest("a")) return;
    if (e.target.closest("video")) return;
    if (e.target.closest("img.home-post-media")) return;

    navigate(`/post/${post.id}`);
  };

  const youtubeUrl = findFirstYouTubeUrl(post.content);
  const youtubeId = youtubeUrl ? extractYouTubeId(youtubeUrl) : null;

  const guestLikeMsg = "Guest mode: please log in to use likes.";
  const guestRepostMsg = "Guest mode: please log in to use reposts.";
  const guestReplyMsg = "Guest mode: please log in to reply.";

  const handleGuestSafeReply = (e) => {
    e.stopPropagation();
    if (isGuest) return alert(guestReplyMsg);
    onReply(post.id);
  };

  const handleGuestSafeRepost = (e) => {
    e.stopPropagation();
    if (isGuest) return alert(guestRepostMsg);
    if (post.repostedByCurrentUser) return;
    onRepost(post.id);
  };

  const handleGuestSafeLike = (e) => {
    e.stopPropagation();
    if (isGuest) return alert(guestLikeMsg);
    onLike(post.id);
  };

  const communityCategory = getPostCommunityCategory(post);

  const handleCommunityClick = (e) => {
    e.stopPropagation();
    if (!communityCategory) return;
    navigate(`/communities?category=${encodeURIComponent(communityCategory)}`);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = getPermalinkUrl(post.id);

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Scoldy post",
          text: "Check out this post on Scoldy:",
          url,
        });
        return;
      }
    } catch (err) {
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        alert("Link copied.");
        return;
      }
    } catch (err) {}

    try {
      window.prompt("Copy this link:", url);
    } catch (err) {
      alert(url);
    }
  };

  return (
    <div
      ref={postRef}
      className="home-post clickable-post"
      onClick={handlePostClick}
      style={{ cursor: "pointer" }}
    >
      <div className="home-post-header">
        <img
          className="home-post-thumbnail"
          src={
            post.thumbnail?.startsWith("http")
              ? post.thumbnail
              : `${API_BASE}${post.thumbnail || "/images/default-thumbnail.jpg"}`
          }
          alt={post.username}
          loading="lazy"
          decoding="async"
          onError={(e) => (e.target.src = "/images/default-thumbnail.jpg")}
          onClick={(e) => {
            e.stopPropagation();
            onNavigateToProfile(post.username);
          }}
          style={{ cursor: "pointer" }}
        />

        <div className="home-post-header-right">
          <div className="home-post-header-line">
            <div className="home-post-user-info">
              {post.postUser?.name && (
  <span
    className="home-post-realname"
    title={post.postUser.name}
  >
    <span className="home-post-realname-text">{post.postUser.name}</span>

    {post.postUser?.Group?.showBadge && (
      <span
        title={post.postUser?.Group?.badgeLabel}
        style={{
  marginLeft: "4px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  verticalAlign: "baseline",
  lineHeight: "1",
  position: "relative",
  top: "-1px",
}}
      >
        {post.postUser?.Group?.badgeType === "circle" && (
          <span
            style={{
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              backgroundColor: "#000",
              color: "#d4af37",
              fontWeight: "bold",
              fontSize: "11px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {post.postUser?.Group?.badgeText}
          </span>
        )}

        {post.postUser?.Group?.badgeType === "star" && (
          <span
            style={{
              color:
                post.postUser?.Group?.badgeColor === "silver"
                  ? "#C0C0C0"
                  : post.postUser?.Group?.badgeColor === "gold"
                  ? "#FFD700"
                  : post.postUser?.Group?.badgeColor === "platinum"
                  ? "#b9c3cf"
                  : "#4da6ff",
              fontSize: "16px",
            }}
          >
            ★
          </span>
        )}

        {post.postUser?.Group?.badgeType === "diamond" && (
          <span
            style={{
              color: "#4fd1ff",
              fontSize: "16px",
            }}
          >
            ♦
          </span>
        )}
      </span>
    )}
  </span>
)}
              <span className="home-post-username" title={`@${post.username}`}>@{post.username}</span>
              <span className="home-post-date">{timeAgo(post.createdAt)}</span>
              {showCommunityLabel && communityCategory && (
                <button
                  type="button"
                  onClick={handleCommunityClick}
                  title={`Open ${getCommunityShortLabel(communityCategory)} community`}
                  aria-label={`Open ${getCommunityShortLabel(communityCategory)} community`}
                  style={{
                    marginLeft: "5px",
                    padding: "1px 5px",
                    border: "1px solid #b8bec8",
                    borderRadius: "999px",
                    background: "#f5f6f8",
                    color: "#4a5568",
                    fontSize: "10px",
                    fontWeight: 700,
                    lineHeight: "1.25",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    position: "relative",
                    top: "2px",
                  }}
                >
                  {getCommunityShortLabel(communityCategory)}
                </button>
              )}
            </div>

            {shouldShowEllipsis && (
              <div
                className="home-post-ellipsis-wrapper"
                ref={dropdownRef}
                onMouseEnter={() => setShowDropdown(true)}
                onMouseLeave={() => setShowDropdown(false)}
                onClick={(e) => e.stopPropagation()}
              >
                <button className="home-post-ellipsis" aria-label="More">
                  {ICON_ELLIPSIS}
                </button>

                {showDropdown && (
                  <div className="home-post-dropdown" onClick={(e) => e.stopPropagation()}>
                    {isCurrentUser && (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onDelete(post.id, currentUser);
                          setShowDropdown(false);
                        }}
                      >
                        {ICON_DELETE} Delete
                      </a>
                    )}

                    {isAdminOrMod && !isCurrentUser && (
                      <>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (window.confirm("Are you sure?")) {
                              onDelete(post.id, currentUser);
                              setShowDropdown(false);
                            }
                          }}
                        >
                          {ICON_DELETE} Admin Delete
                        </a>

                        {Number(currentUser?.group_id) === 1 && (
                          <>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleAdminModeration("warnings"); }}>
                              {ICON_WARN} Warn
                            </a>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleAdminModeration("block"); }}>
                              {ICON_BLOCK} Admin Block User
                            </a>
                          </>
                        )}
                      </>
                    )}

                    {!isCurrentUser && !isGuest && (
                      <>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (onNotInterested) {
                              onNotInterested(post.id);
                            } else {
                              setIsHidden(true);
                            }
                            setShowDropdown(false);
                          }}
                        >
                          {ICON_NOT_INTERESTED} Not Interested
                        </a>

                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            onMuteUser(post.user_id, post);
                            setShowDropdown(false);
                          }}
                        >
                          {ICON_MUTE} Mute
                        </a>

                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (onReport) onReport(post.id, post);
                            else window.alert("Reporting is unavailable on this page.");
                            setShowDropdown(false);
                          }}
                        >
                          {ICON_WARN} Report
                        </a>

                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (onBlockUser) onBlockUser(post.user_id, post);
                            setShowDropdown(false);
                          }}
                        >
                          {ICON_BLOCK} Block User
                        </a>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className="home-post-content"
            onClick={(e) => {
              const link = e.target.closest("a");
              if (!link) return;

              e.stopPropagation();

              const href = link.getAttribute("href") || "";
              if (href.includes("youtube.com") || href.includes("youtu.be")) {
                e.preventDefault();
              }
            }}
          >
            {post.content.length > 300 ? (
              <>
                <p
                  className="home-post-content-text"
                  dangerouslySetInnerHTML={{
                    __html: linkify(post.content.slice(0, 300)) + "...",
                  }}
                />

                <button
                  className="home-show-more-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowModal(post);
                  }}
                >
                  Show more
                </button>
              </>
            ) : (
              <p
                className="home-post-content-text"
                dangerouslySetInnerHTML={{ __html: linkify(post.content) }}
              />
            )}
          </div>
        </div>
      </div>

      {youtubeId ? (
        <div className="home-post-media youtube-embed" onClick={(e) => e.stopPropagation()}>
          {mediaNearViewport ? (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&playsinline=1`}
              title="YouTube video player"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{
                width: "100%",
                aspectRatio: "16 / 9",
                border: "none",
                borderRadius: "12px",
              }}
            />
          ) : (
            <div
              className="home-post-media-placeholder"
              aria-label="Video loads when near the screen"
            />
          )}
        </div>
      ) : (
        post.media_url && (
          <>
            {post.media_url.endsWith(".mp4") ||
            post.media_url.endsWith(".webm") ||
            post.media_url.endsWith(".ogg") ? (
              <video
                ref={videoRef}
                className="home-post-media"
                muted
                loop
                autoPlay={mediaNearViewport}
                playsInline
                preload={mediaNearViewport ? "metadata" : "none"}
                onClick={(e) => e.stopPropagation()}
              >
                <source
                  src={
                    post.media_url.startsWith("http")
                      ? post.media_url
                      : `${API_BASE}${post.media_url}`
                  }
                  type="video/mp4"
                />
              </video>
            ) : (
              <img
                className="home-post-media"
                src={
                  post.media_url.startsWith("http")
                    ? post.media_url
                    : `${API_BASE}${post.media_url}`
                }
                alt="Post media"
                loading="lazy"
                decoding="async"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </>
        )
      )}

      {post.gif_url && (
        <img
          className="home-post-media"
          src={post.gif_url}
          alt="gif"
          loading="lazy"
          decoding="async"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      <div className="home-post-actions" onClick={(e) => e.stopPropagation()}>
        <button className="action-btn" onClick={handleGuestSafeReply} title="Replies">
          <ReplyIcon />
          <span className="action-count">{post.repliesCount || 0}</span>
        </button>

        <button
          className="action-btn"
          onClick={handleGuestSafeRepost}
          title={post.repostedByCurrentUser ? "Reposted" : "Reposts"}
          aria-pressed={!!post.repostedByCurrentUser}
          style={{
            color: post.repostedByCurrentUser ? "#00ba7c" : undefined,
          }}
        >
          <RepostIcon />
          <span className="action-count">{post.repostsCount || 0}</span>
        </button>

        <button
          className="action-btn"
          onClick={handleGuestSafeLike}
          title={post.likedByCurrentUser ? "Liked" : "Likes"}
          aria-pressed={!!post.likedByCurrentUser}
          style={{
            color: post.likedByCurrentUser ? "#e0245e" : undefined,
          }}
        >
          <LikeIcon active={!!post.likedByCurrentUser} />
          <span className="action-count">{post.likes || 0}</span>
        </button>

        <button className="action-btn" disabled title="Views">
          <ViewsIcon />
          <span className="action-count">{localViewCount}</span>
        </button>

        <span className="action-divider" aria-hidden="true" />

        <button className="action-btn action-btn-icon-only" onClick={handleShare} title="Share">
          <ShareIcon />
        </button>

        {extraActions && extraActions}
      </div>
    </div>
  );
}

export default Post;